import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import { alpha, useTheme } from "@mui/material/styles";
import type { NotificationStreamItem } from "@situate/contracts";
import { ConsoleSurface, SeverityAccent, getSeverityAccentColor } from "@/shared/ui";
import { AccessTimeIcon } from "@/shared/ui/console-icons";
import { formatNotificationCreatedAt } from "@/features/deployment-dashboard/notification-center/notification-stream-item-format";

interface LiveNotificationCardProps {
  item: NotificationStreamItem;
  groupedCount?: number;
  selected?: boolean;
  onSelect: (notificationId: string) => void;
}

/** Stable DOM id for a notification row, used to scroll/focus it into view from a popup click. */
export function getNotificationRowDomId(notificationId: string): string {
  return `notification-row-${notificationId}`;
}

/**
 * Compact bell-panel notification card: severity stripe, title, message,
 * type and created time. Shows only contract
 * fields -- no invented zone/category/metrics/trend.
 */
export function LiveNotificationCard({
  item,
  groupedCount = 1,
  selected = false,
  onSelect,
}: LiveNotificationCardProps) {
  const theme = useTheme();
  const stripeColor = getSeverityAccentColor(theme, item.severity);

  return (
    <ButtonBase
      id={getNotificationRowDomId(item.notification_id)}
      onClick={() => onSelect(item.notification_id)}
      aria-pressed={selected}
      aria-label={`View notification: ${item.title}`}
      sx={(buttonTheme) => ({
        display: "block",
        width: "100%",
        textAlign: "left",
        borderRadius: `${buttonTheme.shape.borderRadius}px`,
      })}
    >
      <ConsoleSurface
        sx={{
          px: 2,
          py: 1.75,
          borderLeft: `4px solid ${stripeColor}`,
          border: selected ? `1px solid ${alpha(theme.palette.secondary.main, 0.6)}` : undefined,
        }}
      >
        <Box sx={{ display: "grid", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={item.type === "alert" ? "Alert" : "System"}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.625rem", fontWeight: 700 }}
            />
            <SeverityAccent level={item.severity} label={item.severity.toUpperCase()} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
            {item.title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {item.message}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTimeIcon
              aria-hidden="true"
              sx={{ fontSize: "0.75rem", color: "text.secondary" }}
            />
            <Typography variant="caption" color="text.secondary">
              {formatNotificationCreatedAt(item.created_at)}
            </Typography>
          </Box>
        </Box>
        {groupedCount > 1 && (
          <Box sx={{ display: "flex", mt: 0.75 }}>
            <Chip
              label={`Grouped ${groupedCount} notifications`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.625rem", fontWeight: 700 }}
            />
          </Box>
        )}
      </ConsoleSurface>
    </ButtonBase>
  );
}
